/**
 * The four-eyes evidence packet and its decision controls (ALL-5908,
 * page spec `invoice-resolution-approval`).
 *
 * An approver decides from what is on this panel and nothing else, so every
 * block the spec names is rendered from the service's own payload: the
 * exception, the proposed disposition and its argument, the amounts in base
 * units *and* the formatted form, the constituent payments, the policy/risk
 * reference and the proposal's audit trail.
 *
 * No propose control exists here. The spec's `expected_denies` for every action
 * on this page include `invoice:resolve.propose` / `invoice:refund.propose`, and
 * an approver who could propose would defeat the separation the page exists to
 * enforce. The only controls are the decision ones, and they are rendered only
 * while the proposal is still `PROPOSED`.
 */

import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input, Field } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { StatusDot } from "@/components/ui/status";
import { formatMoney } from "@/features/invoicing/money";
import {
  exceptionStatePresentation,
  exceptionTypePresentation,
  paymentStatePresentation,
  refundStatePresentation,
  resolutionKindPresentation,
  resolutionStatePresentation,
} from "@/features/invoicing/status";
import type {
  Exception,
  Money,
  Payment,
  PrincipalRef,
  Refund,
  Resolution,
} from "@/features/invoicing/types";
import { RESOLUTIONS_COPY as COPY } from "./copy";

/** A money value the way this page always shows it: base units, then the human form. */
function AmountValue({ value }: { value: Money | undefined | null }) {
  if (!value || typeof value.amount !== "string") {
    return <span className="text-sm text-muted">—</span>;
  }
  let display: string | null = null;
  try {
    display = `${formatMoney(value)} ${value.asset?.symbol ?? ""}`.trim();
  } catch {
    // A Money the helper refuses is not a value we may render as a number; the
    // base-unit string the service sent is shown on its own.
    display = null;
  }
  return (
    <span className="flex flex-col">
      <span className="font-mono text-sm text-ink" data-testid="money-base-units">
        {value.amount}
      </span>
      {display && (
        <span className="text-xs text-muted">
          {display} · {COPY.amountDisplayLabel.toLowerCase()}
        </span>
      )}
    </span>
  );
}

function PrincipalValue({ principal }: { principal: PrincipalRef | null }) {
  if (!principal) return <span className="text-sm text-muted">—</span>;
  return (
    <span className="flex flex-col">
      <span className="font-mono text-sm text-ink">{principal.principalId}</span>
      <span className="text-xs text-muted">{principal.type}</span>
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-line px-3 py-2.5 last:border-0 sm:grid-cols-[180px_1fr] sm:gap-3">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

export interface ResolutionPacketProps {
  exception: Exception;
  resolution: Resolution;
  payments: { data: Payment[] | null; loading: boolean; error: { reasonCode: string } | null };
  onReloadPayments: () => void;
  /** The refund an approved decision returned, if this decision produced one. */
  refund: Refund | null;
  pending: "approve" | "reject" | "cancel" | null;
  rejectReason: string;
  onRejectReasonChange: (value: string) => void;
  rejectReasonError: string | null;
  onApprove: () => void;
  onReject: () => void;
  onCancel: () => void;
}

export function ResolutionPacket({
  exception,
  resolution,
  payments,
  onReloadPayments,
  refund,
  pending,
  rejectReason,
  onRejectReasonChange,
  rejectReasonError,
  onApprove,
  onReject,
  onCancel,
}: ResolutionPacketProps) {
  const kind = resolutionKindPresentation(resolution.kind);
  const state = resolutionStatePresentation(resolution.state);
  const exceptionType = exceptionTypePresentation(exception.type);
  const exceptionState = exceptionStatePresentation(exception.state);
  // Only a proposal still awaiting a decision can be decided. A decided one shows
  // its outcome and no controls, which is also what makes the refusal path honest:
  // the controls the approver clicks are ones the service can still act on.
  const decidable = resolution.state === "PROPOSED";

  return (
    <div className="flex flex-col gap-4" data-testid="resolution-packet">
      <Card>
        <CardBody className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{COPY.packetHeading}</CardTitle>
            <Badge variant="info" dot={false}>
              {kind.label}
            </Badge>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted">
              <StatusDot tone={state.tone} />
              {state.label}
            </span>
          </div>
          <div className="font-mono text-xs text-muted" data-testid="packet-resolution-id">
            {resolution.resolutionId}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle>{COPY.exceptionSection}</CardTitle>
          <Row label={COPY.exceptionIdLabel}>
            <span className="font-mono text-sm text-ink">{exception.exceptionId}</span>
          </Row>
          <Row label={COPY.invoiceLabel}>
            <span className="font-mono text-sm text-ink">{exception.invoiceId}</span>
          </Row>
          <Row label={COPY.kindColumn}>
            <span className="inline-flex items-center gap-1.5 text-sm text-ink">
              <StatusDot tone={exceptionType.tone} />
              {exceptionType.label}
            </span>
          </Row>
          <Row label={COPY.stateColumn}>
            <span className="inline-flex items-center gap-1.5 text-sm text-ink">
              <StatusDot tone={exceptionState.tone} />
              {exceptionState.label}
            </span>
          </Row>
          <Row label={COPY.amountColumn}>
            <AmountValue value={exception.amount} />
          </Row>
          <Row label={COPY.paymentIdLabel}>
            <span className="font-mono text-sm text-ink">{exception.paymentId ?? "—"}</span>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle>{COPY.proposalSection}</CardTitle>
          <Row label={COPY.kindColumn}>
            <span className="text-sm text-ink">{kind.label}</span>
          </Row>
          <Row label={COPY.amountColumn}>
            <AmountValue value={resolution.amount} />
          </Row>
          <Row label={COPY.targetInvoiceLabel}>
            <span className="font-mono text-sm text-ink">{resolution.targetInvoiceId ?? "—"}</span>
          </Row>
          <Row label={COPY.proposedByLabel}>
            <PrincipalValue principal={resolution.proposedBy} />
          </Row>
          <Row label={COPY.createdAtLabel}>
            <span className="text-sm text-ink">{resolution.createdAt}</span>
          </Row>
          <Row label={COPY.reasonLabel}>
            <span className="text-sm text-ink" data-testid="resolution-reason">
              {resolution.reason ?? COPY.noReason}
            </span>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle>{COPY.paymentSection}</CardTitle>
          {payments.loading && <p className="text-sm text-muted">Loading payments…</p>}
          {!payments.loading && payments.error && (
            <div className="flex flex-col items-start gap-2">
              <p className="text-sm text-danger">
                {`Payments could not be read (${payments.error.reasonCode}).`}
              </p>
              <Button variant="secondary" size="sm" onClick={onReloadPayments}>
                Try again
              </Button>
            </div>
          )}
          {!payments.loading && !payments.error && payments.data && payments.data.length === 0 && (
            <EmptyState title={COPY.paymentsEmptyTitle} description={COPY.paymentsEmptyBody} />
          )}
          {!payments.loading && !payments.error && payments.data && payments.data.length > 0 && (
            <TableWrapper>
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                    <th className="px-3 py-2.5">{COPY.paymentIdLabel}</th>
                    <th className="px-3 py-2.5">{COPY.stateColumn}</th>
                    <th className="px-3 py-2.5">{COPY.amountColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.data.map((payment) => {
                    const paymentState = paymentStatePresentation(payment.state);
                    const constituent = payment.paymentId === exception.paymentId;
                    return (
                      <tr
                        key={payment.paymentId}
                        className="border-b border-line last:border-0"
                        data-constituent={constituent ? "true" : undefined}
                      >
                        <td className="px-3 py-2.5 font-mono text-sm text-ink">{payment.paymentId}</td>
                        <td className="px-3 py-2.5 text-sm text-ink">
                          <span className="inline-flex items-center gap-1.5">
                            <StatusDot tone={paymentState.tone} />
                            {paymentState.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <AmountValue value={payment.amount} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle>{COPY.policySection}</CardTitle>
          <Row label={COPY.policyRefLabel}>
            <span className="font-mono text-sm text-ink">
              {resolution.policyEvalRef ?? COPY.noPolicyRef}
            </span>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-2">
          <CardTitle>{COPY.auditSection}</CardTitle>
          <ul className="flex flex-col gap-2 text-sm text-ink" data-testid="resolution-audit-trail">
            <li className="flex flex-wrap gap-x-2">
              <span className="text-muted">{COPY.proposedByLabel}</span>
              <span className="font-mono">{resolution.proposedBy.principalId}</span>
              <span className="text-muted">{resolution.createdAt}</span>
            </li>
            {resolution.approvedBy && (
              <li className="flex flex-wrap gap-x-2">
                <span className="text-muted">{COPY.approvedByLabel}</span>
                <span className="font-mono">{resolution.approvedBy.principalId}</span>
                <span className="text-muted">{resolution.decidedAt ?? resolution.updatedAt}</span>
              </li>
            )}
            {!resolution.approvedBy && resolution.decidedAt && (
              <li className="flex flex-wrap gap-x-2">
                <span className="text-muted">{COPY.decidedAtLabel}</span>
                <span className="text-muted">{resolution.decidedAt}</span>
              </li>
            )}
          </ul>
        </CardBody>
      </Card>

      {refund && (
        <Card>
          <CardBody className="flex flex-col gap-2" data-testid="resolution-refund">
            <CardTitle>{COPY.refundSection}</CardTitle>
            <Row label={COPY.refundStateLabel}>
              <span className="inline-flex items-center gap-1.5 text-sm text-ink">
                <StatusDot tone={refundStatePresentation(refund.state).tone} />
                {refundStatePresentation(refund.state).label}
              </span>
            </Row>
            <Row label={COPY.amountColumn}>
              <AmountValue value={refund.amount} />
            </Row>
            <Row label={COPY.refundDestinationLabel}>
              <span className="text-sm text-ink">
                {refund.destinationAddress
                  ? `${refund.destinationAddress}${refund.destinationNetwork ? ` (${refund.destinationNetwork})` : ""}`
                  : COPY.refundNoDestination}
              </span>
            </Row>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle>{COPY.queueHeading}</CardTitle>
          {decidable ? (
            <>
              <p className="text-xs text-muted">{COPY.decisionNote}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={onApprove} loading={pending === "approve"} disabled={pending !== null}>
                  {pending === "approve" ? COPY.approvingLabel : COPY.approveLabel}
                </Button>
                <Button
                  variant="secondary"
                  onClick={onCancel}
                  loading={pending === "cancel"}
                  disabled={pending !== null}
                >
                  {pending === "cancel" ? COPY.cancellingLabel : COPY.cancelLabel}
                </Button>
              </div>
              <div className="flex flex-col gap-2 border-t border-line pt-3 sm:flex-row sm:items-end">
                <Field
                  label={COPY.rejectReasonLabel}
                  htmlFor="resolution-reject-reason"
                  hint={COPY.rejectReasonHint}
                  error={rejectReasonError ?? undefined}
                >
                  <Input
                    id="resolution-reject-reason"
                    value={rejectReason}
                    onChange={(event) => onRejectReasonChange(event.target.value)}
                  />
                </Field>
                <Button
                  variant="secondary"
                  onClick={onReject}
                  loading={pending === "reject"}
                  disabled={pending !== null}
                >
                  {pending === "reject" ? COPY.rejectingLabel : COPY.rejectLabel}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted">{COPY.decidedElsewhere}</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
